import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';
import { 
  ChevronLeft, User, Phone, MessageCircle, Calendar, MapPin, 
  ArrowRight, VideoIcon, MoreVertical, Smile, Mic, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDIAN_NAMES = [
  'Rajesh Kumar', 'Priya Sharma', 'Amit Patel', 'Sneha Reddy', 'Vikram Singh',
  'Anjali Verma', 'Rahul Gupta', 'Kavita Desai', 'Sanjay Mehta', 'Pooja Nair'
];

export const PatientView = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { settings } = useSettings();
  const [messages, setMessages] = useState([]);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);
  const [waitTime, setWaitTime] = useState(null);
  const [callingAnimation, setCallingAnimation] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [phoneBuzz, setPhoneBuzz] = useState(false);

  const getRandomName = () => {
    return INDIAN_NAMES[Math.floor(Math.random() * INDIAN_NAMES.length)];
  };

  const getRandomPhone = () => {
    const num = Math.floor(Math.random() * 90000000) + 10000000;
    return `+91 ${num.toString().slice(0, 5)} ${num.toString().slice(5)}`;
  };

  const addMessage = (content) => {
    const newMessage = {
      id: Date.now(),
      content,
      timestamp: new Date().toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
    
    setMessages(prev => [...prev, newMessage]);
    setPhoneBuzz(true);
    setTimeout(() => setPhoneBuzz(false), 220);
  };

  const addPatientToQueue = async (entryMethod, appointmentTime = null) => {
    try {
      const patientName = getRandomName();
      const patientPhone = getRandomPhone();
      
      const response = await axios.post(
        `${API}/queue`,
        {
          patient_name: patientName,
          patient_phone: patientPhone
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const patient = response.data;
      setCurrentPatient(patient);
      setQueuePosition(3); // Simulated
      setWaitTime(12); // Simulated

      // Send WhatsApp message
      setTimeout(() => {
        const message = `Hello ${patientName.split(' ')[0]}!\n\nYou've joined the queue at ${settings.clinic_name}.\n\nYour token: *${patient.token_number}*\nPosition: *3rd in queue*\nEstimated wait: *~12 minutes*\n\nWe'll notify you when it's your turn.`;
        addMessage(message);
      }, 1200);

      toast.success(`Patient added · ${patient.token_number} issued`);
      
      return patient;
    } catch (error) {
      console.error('Error adding patient:', error);
      toast.error('Failed to add patient');
    }
  };

  const handleCallClick = async () => {
    setCallingAnimation(true);
    setTimeout(() => setCallingAnimation(false), 1200);
    await addPatientToQueue('Call');
  };

  const handleWhatsAppClick = async () => {
    setCallingAnimation(true);
    setTimeout(() => setCallingAnimation(false), 1200);
    await addPatientToQueue('WhatsApp');
  };

  const handleWalkInClick = async () => {
    const patient = await addPatientToQueue('Walk-in');
    if (patient) {
      toast.success('Patient registered at reception');
    }
  };

  return (
    <div className="min-h-screen" data-testid="patient-view">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 glass-primary" style={{ height: '68px' }}>
        <div className="h-full flex items-center justify-between px-7">
          <button
            onClick={() => navigate('/personas')}
            className="flex items-center gap-2 font-['Inter'] text-[14px] font-medium transition-colors duration-180 hover:text-[#1C1917]"
            style={{ color: '#44403C' }}
          >
            <ChevronLeft size={18} strokeWidth={1.75} />
            Back to roles
          </button>

          <h1 className="font-['Sora'] text-base font-semibold" style={{ color: '#1C1917' }}>
            {settings.clinic_name}
          </h1>

          <div className="glass-subtle flex items-center gap-2 px-3 py-1.5 rounded-full">
            <User size={15} strokeWidth={1.75} style={{ color: 'var(--color-slate)', opacity: 0.7 }} />
            <span className="font-['Inter'] text-sm font-medium" style={{ color: '#44403C' }}>
              Patient view
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-app" style={{ paddingTop: '100px', paddingBottom: '32px' }}>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Contact Actions */}
          <div style={{ width: '340px' }}>
            <div className="mb-6">
              <h2 className="font-['Sora'] text-[26px] font-bold mb-2" style={{ color: '#1C1917' }}>
                Reach the clinic
              </h2>
              <p className="font-['Inter'] text-[14px]" style={{ color: '#78716C' }}>
                Choose how you'd like to connect
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <ContactCard
                icon={Phone}
                title={settings.clinic_landline}
                subtitle="Tap to call directly"
                onClick={handleCallClick}
                isAnimating={callingAnimation}
                primary
              />

              <ContactCard
                icon={MessageCircle}
                title={settings.clinic_phone}
                subtitle="Tap to message"
                onClick={handleWhatsAppClick}
                isAnimating={callingAnimation}
                primary
              />

              <ContactCard
                icon={Calendar}
                title="Book a specific slot"
                subtitle="Choose date and time"
                onClick={() => setShowAppointmentModal(true)}
              />

              <ContactCard
                icon={MapPin}
                title="Walk into the clinic"
                subtitle="Reception will register you on arrival"
                onClick={handleWalkInClick}
                subtle
              />
            </div>
          </div>

          {/* Right Column - Phone Mockup */}
          <div className="flex-1 flex flex-col items-center gap-6">
            {/* Status Strip */}
            <div className="glass-subtle px-4 py-2 rounded-full">
              <span className="font-['DM_Mono'] text-xs" style={{ color: '#44403C' }}>
                {currentPatient ? (
                  `Position: ${queuePosition} · Wait: ~${waitTime} min`
                ) : (
                  'Position: — · Wait: —'
                )}
              </span>
            </div>

            {/* iPhone Mockup */}
            <IPhoneMockup messages={messages} buzz={phoneBuzz} clinicName={settings.clinic_name} />
          </div>
        </div>
      </div>

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <AppointmentModal
          onClose={() => setShowAppointmentModal(false)}
          onConfirm={async (slot) => {
            const patient = await addPatientToQueue('Appointment', slot);
            if (patient) {
              const message = `Appointment confirmed ✓\n\nDate: ${slot.date}\nTime: ${slot.time}\nToken: *${patient.token_number}*\n\nPlease arrive 5 minutes early.`;
              setTimeout(() => addMessage(message), 500);
              toast.success(`Appointment booked for ${slot.time}`);
            }
            setShowAppointmentModal(false);
          }}
        />
      )}
    </div>
  );
};

const ContactCard = ({ icon: Icon, title, subtitle, onClick, isAnimating, primary, subtle }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        ${
          subtle ? 'glass-subtle' : primary ? 'glass-primary' : 'glass-subtle'
        }
        rounded-[var(--radius-lg)] p-4.5 flex items-center gap-4
        hover-lift interactive transition-all duration-180
        ${isAnimating ? 'animate-pulse-ring' : ''}
      `}
      style={{
        border: isHovered ? '1px solid rgba(212, 120, 74, 0.2)' : undefined
      }}
    >
      {/* Icon Container */}
      <div
        className="flex-shrink-0 rounded-full flex items-center justify-center"
        style={{
          width: '48px',
          height: '48px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.68) 0%, rgba(255,255,255,0.45) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.75)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <Icon size={22} strokeWidth={1.75} style={{ color: 'var(--color-slate)', opacity: 0.72 }} />
      </div>

      {/* Text */}
      <div className="flex-1 text-left">
        <div className="font-['DM_Mono'] text-[15px] font-medium mb-0.5" style={{ color: '#1C1917' }}>
          {title}
        </div>
        <div className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>
          {subtitle}
        </div>
      </div>

      {/* Arrow */}
      <ArrowRight
        size={16}
        strokeWidth={1.75}
        className={`transition-all duration-180 ${isHovered ? 'translate-x-1' : ''}`}
        style={{ color: isHovered ? '#B5612E' : '#78716C' }}
      />
    </button>
  );
};

const IPhoneMockup = ({ messages, buzz, clinicName }) => {
  const chatRef = React.useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  // Initial of the contact for the avatar
  const initial = (clinicName || 'C').replace(/^Dr\.?\s+/i, '').trim().charAt(0).toUpperCase();

  return (
    <div
      className={`relative transition-transform duration-220 ${buzz ? 'animate-buzz' : ''}`}
      style={{ width: '300px' }}
    >
      {/* Phone Shell */}
      <div
        className="relative"
        style={{
          background: 'linear-gradient(180deg, #1A1410 0%, #0F0D0A 100%)',
          border: '8px solid #1C1714',
          borderRadius: '46px',
          boxShadow: `
            inset 0 1.5px 0 0 rgba(255,255,255,0.1),
            inset 0 0 0 0.5px rgba(255,255,255,0.05),
            inset 0 -2px 4px 0 rgba(0,0,0,0.3),
            0 2px 8px rgba(28,25,23,0.12),
            0 8px 16px rgba(28,25,23,0.10),
            0 16px 32px rgba(28,25,23,0.08),
            0 24px 48px rgba(28,25,23,0.06)
          `,
          overflow: 'hidden'
        }}
      >
        {/* Screen Content */}
        <div
          style={{
            background: '#EBE4DC',
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {/* Status bar — uses left + right gutters so the time and icons sit beside the Dynamic Island (not behind it) */}
          <div
            style={{
              position: 'relative',
              height: '44px',
              padding: '12px 22px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'white',
              zIndex: 10,
            }}
          >
            <span
              className="font-['DM_Mono']"
              style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1, letterSpacing: '0.01em' }}
            >
              {currentTime}
            </span>

            {/* Dynamic Island sits in the gap between time and icons */}
            <div
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '92px',
                height: '26px',
                background: '#0A0907',
                borderRadius: '13px',
              }}
            />

            <div className="flex items-center gap-1.5" style={{ lineHeight: 0 }}>
              <SignalIcon />
              <WifiIcon />
              <BatteryIcon />
            </div>
          </div>

          {/* WhatsApp Header — single-row layout for clinic name */}
          <div
            style={{
              minHeight: '58px',
              background: 'linear-gradient(180deg, #075E54 0%, #064F46 100%)',
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: '1px solid rgba(0,0,0,0.15)'
            }}
          >
            <ChevronLeft size={18} style={{ color: 'white', flexShrink: 0 }} />
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: 'Sora',
                fontWeight: 700,
                fontSize: '14px',
                flexShrink: 0,
              }}
            >
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="font-['Sora'] font-semibold"
                style={{
                  color: 'white',
                  fontSize: '13px',
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {clinicName || 'Clinic'}
              </div>
              <div
                className="font-['Inter']"
                style={{ color: 'rgba(255,255,255,0.75)', fontSize: '10px', lineHeight: 1.2, marginTop: '2px' }}
              >
                online
              </div>
            </div>
            <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
              <VideoIcon size={16} style={{ color: 'rgba(255,255,255,0.85)' }} />
              <Phone size={16} style={{ color: 'rgba(255,255,255,0.85)' }} />
              <MoreVertical size={16} style={{ color: 'rgba(255,255,255,0.85)' }} />
            </div>
          </div>

          {/* Chat Area */}
          <div
            ref={chatRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 10px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              minHeight: '420px',
            }}
            className="scrollbar-hide"
          >
            {messages.map((msg) => (
              <MessageBubble key={msg.id} content={msg.content} timestamp={msg.timestamp} />
            ))}
          </div>

          {/* Input Bar */}
          <div
            style={{
              minHeight: '54px',
              background: 'rgba(244,240,235,0.96)',
              borderTop: '1px solid rgba(0,0,0,0.05)',
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Smile size={22} style={{ color: '#667781', flexShrink: 0 }} />
            <div
              style={{
                flex: 1,
                height: '38px',
                background: 'white',
                borderRadius: '19px',
                padding: '0 14px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                color: '#667781'
              }}
            >
              Type a message
            </div>
            <Mic size={22} style={{ color: '#667781', flexShrink: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ content, timestamp }) => {
  const renderContent = (text) => {
    // Convert *text* to bold
    const parts = text.split(/\*([^*]+)\*/);
    return parts.map((part, i) => 
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  return (
    <div className="animate-slide-up" style={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '4px 14px 14px 14px',
          padding: '9px 13px 7px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.10)'
        }}
      >
        <div
          className="font-['Inter'] whitespace-pre-line"
          style={{ color: '#111B21', fontSize: '14.5px', lineHeight: 1.45 }}
        >
          {renderContent(content)}
        </div>
        <div className="flex items-center justify-end gap-1 mt-1">
          <span style={{ fontSize: '11px', color: 'rgba(0,0,0,0.45)' }}>{timestamp}</span>
          <CheckCheck size={13} style={{ color: '#53BDEB' }} />
        </div>
      </div>
    </div>
  );
};

const SignalIcon = () => (
  <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
    <rect x="0" y="8" width="2" height="4" fill="white" opacity="0.95" />
    <rect x="4" y="6" width="2" height="6" fill="white" opacity="0.95" />
    <rect x="8" y="3" width="2" height="9" fill="white" opacity="0.95" />
    <rect x="12" y="0" width="2" height="12" fill="white" opacity="0.95" />
  </svg>
);

const WifiIcon = () => (
  <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
    <path d="M8 12C8.55 12 9 11.55 9 11C9 10.45 8.55 10 8 10C7.45 10 7 10.45 7 11C7 11.55 7.45 12 8 12Z" fill="white" opacity="0.95" />
    <path d="M8 8C9.1 8 10 8.9 10 10H6C6 8.9 6.9 8 8 8Z" fill="white" opacity="0.95" />
    <path d="M8 4C10.21 4 12 5.79 12 8H4C4 5.79 5.79 4 8 4Z" fill="white" opacity="0.95" />
  </svg>
);

const BatteryIcon = () => (
  <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
    <rect x="0" y="2" width="20" height="8" rx="2" stroke="white" strokeOpacity="0.95" fill="none" />
    <rect x="2" y="4" width="12.8" height="4" rx="1" fill="#4ADE80" />
    <rect x="21" y="4.5" width="2" height="3" rx="0.5" fill="white" fillOpacity="0.95" />
  </svg>
);

const AppointmentModal = ({ onClose, onConfirm }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const dates = [
    { label: 'Today', value: 'Today, May 16' },
    { label: 'Tomorrow', value: 'Tomorrow, May 17' },
    { label: 'Wed 18', value: 'Wed, May 18' },
    { label: 'Thu 19', value: 'Thu, May 19' },
    { label: 'Fri 20', value: 'Fri, May 20' }
  ];

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(28, 25, 23, 0.5)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="glass-elevated rounded-[var(--radius-2xl)] p-8 max-w-md w-full animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-['Sora'] text-2xl font-bold mb-2" style={{ color: '#1C1917' }}>
          Book Appointment
        </h2>
        <p className="font-['Inter'] text-sm mb-6" style={{ color: '#78716C' }}>
          Choose your preferred date and time
        </p>

        <div className="mb-6">
          <label className="text-overline mb-3 block">SELECT DATE</label>
          <div className="flex gap-2 overflow-x-auto">
            {dates.map((date) => (
              <button
                key={date.value}
                onClick={() => setSelectedDate(date.value)}
                className={`
                  px-4 py-2 rounded-[var(--radius-md)] font-['Inter'] text-sm font-medium
                  transition-all duration-180 whitespace-nowrap
                  ${
                    selectedDate === date.value
                      ? 'bg-copper-gradient text-white'
                      : 'glass-subtle hover:bg-[rgba(61,90,128,0.08)]'
                  }
                `}
                style={{
                  color: selectedDate === date.value ? 'white' : '#44403C'
                }}
              >
                {date.label}
              </button>
            ))}
          </div>
        </div>

        {selectedDate && (
          <div className="mb-6">
            <label className="text-overline mb-3 block">SELECT TIME</label>
            <div className="grid grid-cols-3 gap-2">
              {timeSlots.map((time) => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`
                    py-2 rounded-[var(--radius-md)] font-['Inter'] text-sm font-medium
                    transition-all duration-180
                    ${
                      selectedTime === time
                        ? 'bg-copper-gradient text-white'
                        : 'glass-subtle hover:bg-[rgba(61,90,128,0.08)]'
                    }
                  `}
                  style={{
                    color: selectedTime === time ? 'white' : '#44403C'
                  }}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedDate && selectedTime && (
          <button
            onClick={() => onConfirm({ date: selectedDate, time: selectedTime })}
            className="w-full bg-copper-gradient text-white rounded-[var(--radius-xl)] h-[52px] font-['Sora'] font-semibold hover-lift"
          >
            Confirm Booking
          </button>
        )}
      </div>
    </div>
  );
};

export default PatientView;